import { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowCounterClockwise, FloppyDisk, Pencil } from "@phosphor-icons/react";
import { CATEGORIES } from "@/lib/locations";

const TENNIS_LADDER = ["0", "15", "30", "40", "AD", "JUEGO"];

/**
 * Panel interactivo: marcador grande tipo "tennis ladder" 0/15/30/40/AD/Juego.
 * Permite editar inline: nombres de jugadores, liga, categoría, día de retas, asignación de cancha y club.
 * Admin/Directivo/Jugador del partido pueden editar (con permisos del backend).
 */
export default function InteractiveScorePanel({ match, canEdit, onChanged, leagues = [], clubs = [], allUsers = [] }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialForm = useMemo(() => ({
    p1_p1_name: match.pair1.p1_name || "",
    p1_p1_id: match.pair1.p1_id || "",
    p1_p2_name: match.pair1.p2_name || "",
    p1_p2_id: match.pair1.p2_id || "",
    p2_p1_name: match.pair2.p1_name || "",
    p2_p1_id: match.pair2.p1_id || "",
    p2_p2_name: match.pair2.p2_name || "",
    p2_p2_id: match.pair2.p2_id || "",
    league_id: match.league_id || "",
    category: match.category || "",
    reta_day: match.reta_day || "",
    club_id: match.club_id || "",
    court_name: match.court_name || "",
  }), [match]);
  const [form, setForm] = useState(initialForm);

  const isFinalished = match.status !== "en_curso";
  const isTb = !!match.is_tiebreak;
  const tbPts = match.tiebreak_points || {};
  const score1 = isTb ? String(tbPts.pair1 ?? 0) : String(match.current_game?.pair1 ?? "0");
  const score2 = isTb ? String(tbPts.pair2 ?? 0) : String(match.current_game?.pair2 ?? "0");

  const addPoint = async (pair) => {
    try { await api.post(`/matches/${match.id}/point`, { pair }); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const undoGame = async () => {
    try { await api.post(`/matches/${match.id}/undo-point`); toast.success("Juego actual reiniciado"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveMeta = async () => {
    setSaving(true);
    try {
      await api.put(`/matches/admin/${match.id}`, {
        pair1: { p1_id: form.p1_p1_id || null, p1_name: form.p1_p1_name, p2_id: form.p1_p2_id || null, p2_name: form.p1_p2_name },
        pair2: { p1_id: form.p2_p1_id || null, p1_name: form.p2_p1_name, p2_id: form.p2_p2_id || null, p2_name: form.p2_p2_name },
        league_id: form.league_id || null,
        category: form.category || null,
        reta_day: form.reta_day || null,
        club_id: form.club_id || null,
        court_name: form.court_name || null,
      });
      toast.success("Datos guardados");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const clubObj = clubs.find((c) => c.id === form.club_id);
  const availableCourts = clubObj?.court_names || [];

  // For tennis ladder (Drive): map score string → index in ladder
  const ladderIdx = (s) => {
    const i = TENNIS_LADDER.indexOf(String(s).toUpperCase());
    return i >= 0 ? i : 0;
  };

  return (
    <div className="card-tactical p-4 space-y-4" data-testid="interactive-score-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Marcador interactivo</p>
          <p className="text-[10px] text-gray-500">Score dinámico · {isTb ? "Tiebreak" : "Set normal"}</p>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditing((e) => !e)} className={`text-[10px] uppercase font-bold h-8 ${editing ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15"}`} data-testid="score-meta-toggle">
            <Pencil size={12} className="mr-1" /> {editing ? "Cerrar" : "Editar datos"}
          </Button>
        )}
      </div>

      {/* Tennis ladder visual */}
      {!isTb && (
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
          {[1, 2].map((pair, idx) => {
            const score = pair === 1 ? score1 : score2;
            const li = ladderIdx(score);
            const accent = pair === 1 ? "#CCFF00" : "#FF9500";
            return (
              <div key={pair} className={idx === 1 ? "col-start-3 col-end-4" : "col-start-1 col-end-2"} data-testid={`ladder-pair${pair}`}>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2 text-center" style={{ color: accent }}>Pareja {pair}</div>
                <div className="space-y-1">
                  {TENNIS_LADDER.map((step, i) => {
                    const isCurrent = i === li;
                    const isPast = i < li;
                    return (
                      <div
                        key={step}
                        className={`flex items-center justify-between px-3 py-2 rounded font-mono text-sm transition-all ${isCurrent ? "border-2 font-black scale-105" : isPast ? "border opacity-60" : "border opacity-30"}`}
                        style={{
                          borderColor: isCurrent ? accent : "#333",
                          backgroundColor: isCurrent ? `${accent}22` : "transparent",
                          color: isCurrent ? accent : isPast ? "#999" : "#555",
                        }}
                        data-testid={`ladder-pair${pair}-step-${step}`}
                      >
                        <span className="text-[9px] uppercase">{i === TENNIS_LADDER.length - 1 ? "Juego" : `Punto ${i + 1}`}</span>
                        <span className="font-['Barlow_Condensed'] font-black text-2xl">{step}</span>
                      </div>
                    );
                  })}
                </div>
                {canEdit && match.status === "en_curso" && (
                  <Button onClick={() => addPoint(pair)} className="w-full mt-2 font-bold uppercase tracking-wider text-xs h-12" style={{ backgroundColor: accent, color: "#000" }} data-testid={`ladder-pair${pair}-add`}>
                    +1 punto
                  </Button>
                )}
              </div>
            );
          })}
          <div className="text-center self-stretch flex flex-col items-center justify-center min-w-[60px]">
            <div className="font-['Barlow_Condensed'] text-5xl text-gray-700 font-black">VS</div>
            <div className="text-[9px] text-gray-500 uppercase mt-1">Set actual</div>
            <div className="font-mono text-lg text-white mt-1">{match.current_set_games?.pair1 ?? 0} · {match.current_set_games?.pair2 ?? 0}</div>
          </div>
        </div>
      )}

      {/* Tiebreak counter */}
      {isTb && (
        <div className="grid grid-cols-2 gap-3">
          {[1, 2].map((pair) => {
            const score = pair === 1 ? score1 : score2;
            const accent = pair === 1 ? "#CCFF00" : "#FF9500";
            return (
              <div key={pair} className="text-center bg-[#0A0A0A] border-2 p-4 rounded" style={{ borderColor: accent + "55" }} data-testid={`tb-pair${pair}`}>
                <div className="text-[10px] tracking-widest uppercase font-bold" style={{ color: accent }}>Pareja {pair} · TB</div>
                <div className="font-['Barlow_Condensed'] text-7xl font-black" style={{ color: accent }}>{score}</div>
                {canEdit && (
                  <Button onClick={() => addPoint(pair)} className="w-full mt-2 font-bold uppercase text-xs h-10" style={{ backgroundColor: accent, color: "#000" }} data-testid={`tb-pair${pair}-add`}>
                    +1 TB
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && match.status === "en_curso" && (
        <Button onClick={undoGame} variant="outline" className="w-full border-white/10 text-gray-400 hover:bg-white/5 uppercase text-xs" data-testid="ladder-undo">
          <ArrowCounterClockwise size={14} className="mr-1" /> Reiniciar juego actual (0/0)
        </Button>
      )}

      {/* Inline meta editor */}
      {editing && (
        <div className="space-y-3 pt-3 border-t border-white/5" data-testid="score-meta-editor">
          {/* Players */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { k: "p1_p1", label: "Pareja 1 · J1", color: "text-[#CCFF00]" },
              { k: "p1_p2", label: "Pareja 1 · J2", color: "text-[#CCFF00]" },
              { k: "p2_p1", label: "Pareja 2 · J1", color: "text-orange-400" },
              { k: "p2_p2", label: "Pareja 2 · J2", color: "text-orange-400" },
            ].map(({ k, label, color }) => (
              <div key={k}>
                <p className={`text-[9px] uppercase tracking-widest font-bold mb-1 ${color}`}>{label}</p>
                <Select value={form[`${k}_id`] || "__guest"} onValueChange={(v) => {
                  if (v === "__guest") { setF(`${k}_id`, ""); return; }
                  const u = allUsers.find((x) => x.id === v);
                  setForm((f) => ({ ...f, [`${k}_id`]: v, [`${k}_name`]: u?.name || f[`${k}_name`] }));
                }}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid={`meta-${k}-select`}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                    <SelectItem value="__guest">Invitado (libre)</SelectItem>
                    {allUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={form[`${k}_name`]} onChange={(e) => setF(`${k}_name`, e.target.value)} placeholder="Nombre" className="bg-[#0A0A0A] border-white/15 h-9 text-xs mt-1" data-testid={`meta-${k}-name`} />
              </div>
            ))}
          </div>

          {/* League / Category / Reta day */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-1 text-gray-400">Liga</p>
              <Select value={form.league_id || "__none"} onValueChange={(v) => setF("league_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid="meta-league"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                  <SelectItem value="__none">— sin liga —</SelectItem>
                  {leagues.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-1 text-gray-400">Categoría</p>
              <Select value={form.category || "__none"} onValueChange={(v) => setF("category", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid="meta-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                  <SelectItem value="__none">— sin categoría —</SelectItem>
                  {(CATEGORIES || []).map((c) => <SelectItem key={typeof c === "string" ? c : c.value} value={typeof c === "string" ? c : c.value}>{typeof c === "string" ? c : c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-1 text-gray-400">Día de retas</p>
              <Input value={form.reta_day} onChange={(e) => setF("reta_day", e.target.value)} placeholder="Ej. Martes 19:00" className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid="meta-retaday" />
            </div>
          </div>

          {/* Club + Court */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-1 text-gray-400">Club</p>
              <Select value={form.club_id || "__none"} onValueChange={(v) => setF("club_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid="meta-club"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                  <SelectItem value="__none">— sin club —</SelectItem>
                  {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest font-bold mb-1 text-gray-400">Cancha</p>
              <Select value={form.court_name || "__none"} onValueChange={(v) => setF("court_name", v === "__none" ? "" : v)} disabled={!form.club_id || availableCourts.length === 0}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/15 h-9 text-xs" data-testid="meta-court"><SelectValue placeholder={!form.club_id ? "Elige club" : availableCourts.length === 0 ? "Sin canchas" : "Elegir cancha"} /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-60">
                  <SelectItem value="__none">— sin asignar —</SelectItem>
                  {availableCourts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { setForm(initialForm); setEditing(false); }} className="border-white/15 text-xs uppercase">Cancelar</Button>
            <Button onClick={saveMeta} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-xs uppercase" data-testid="meta-save">
              <FloppyDisk size={12} className="mr-1" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
